import { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import moment from "moment";
import TransparentLoader from "../../../components/transparent-loader/TransparentLoader";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from "@tanstack/react-table";

const trackingTypes = [
  { label: "AWB No.", value: "awb" },
  { label: "Forwarding No.", value: "forwarding" },
  { label: "Reference No.", value: "reference" },
];

const getEventIcon = (state?: string) => {
  const lower = (state || "").toLowerCase();

  if (lower.includes("delivered")) return <span className="text-green-600">📦</span>;
  if (lower.includes("out_for_delivery") || lower.includes("out for delivery"))
    return <span className="text-blue-600">🚚</span>;
  if (lower.includes("in_transit") || lower.includes("in transit"))
    return <span className="text-yellow-600">🚛</span>;
  if (lower.includes("picked_up") || lower.includes("picked up"))
    return <span className="text-indigo-600">✅</span>;
  if (lower.includes("manifest") || lower.includes("booked"))
    return <span className="text-purple-600">📝</span>;

  return <span className="text-gray-600">📍</span>;
};

const getEventBgClass = (state?: string) => {
  const lower = (state || "").toLowerCase();

  if (lower.includes("delivered")) return "bg-green-200 text-green-700";
  if (lower.includes("out_for_delivery") || lower.includes("out for delivery"))
    return "bg-blue-100 text-blue-700";
  if (lower.includes("in_transit") || lower.includes("in transit"))
    return "bg-yellow-100 text-yellow-800";
  if (lower.includes("picked_up") || lower.includes("picked up"))
    return "bg-indigo-100 text-indigo-700";
  if (lower.includes("manifest") || lower.includes("booked"))
    return "bg-purple-100 text-purple-700";

  return "bg-gray-100 text-gray-700";
};

interface ITrackingFormValues {
  trackingType: string;
  trackingNumber: string;
}

interface IScanDetail {
  scan_date?: string;
  action?: string;
  state?: string;
  scan_location?: string;
  remarks?: string;
}

export default function PublicTracking() {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [trackingResult, setTrackingResult] = useState<any>(null);

  const [trackingTypeOpen, setTrackingTypeOpen] = useState(false);

  const initialValues: ITrackingFormValues = {
    trackingType: "awb",
    trackingNumber: "",
  };

  const validationSchema = Yup.object().shape({
    trackingType: Yup.string().required("Tracking type is required"),
    trackingNumber: Yup.string().required("Tracking number is required"),
  });

  const handleSubmit = async (values: ITrackingFormValues) => {
    setLoading(true);
    setErrorMessage(null);
    setTrackingResult(null);

    const trackingNo = values.trackingNumber.trim();
    if (!trackingNo) {
      setErrorMessage("Please enter a tracking number.");
      setLoading(false);
      return;
    }

    const publicAxios = axios.create({
      headers:{},
      withCredentials: false
    })

    const apiUrl = `https://api.tqslogistics.co.in/api/v1/operations/awb/track/${trackingNo}`;

    try {
      const response = await axios.get(apiUrl);
      if (response.data?.status) {
        setTrackingResult(response.data.data);
      } else {
        setErrorMessage(response.data?.message || "No tracking data found.");
      }
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      setErrorMessage(backendMsg || "Unable to fetch tracking info.");
    } finally {
      setLoading(false);
    }
  };

  const data: IScanDetail[] = trackingResult?.scan_details
    ? [...trackingResult.scan_details].sort(
        (a, b) =>
          moment.utc(b.scan_date).valueOf() - moment.utc(a.scan_date).valueOf()
      )
    : [];

  const columns: ColumnDef<IScanDetail>[] = [
    {
      header: "Date",
      accessorKey: "scan_date",
      cell: (info) =>
        info.getValue()
          ? moment
              .utc(info.getValue() as string)
              .utcOffset("+05:30")
              .format("DD MMM YYYY, hh:mm A")
          : "",
    },
    {
      header: "Event Description",
      accessorKey: "action",
      cell: (info) => {
        const row = info.row.original;
        return row.action ? (
          <div className="flex items-center gap-2">
            {getEventIcon(row.state)}
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getEventBgClass(
                row.state
              )}`}
            >
              {row.action}
            </span>
          </div>
        ) : (
          ""
        );
      },
    },
    {
      header: "Location",
      accessorKey: "scan_location",
      cell: (info) => info.getValue() || "",
    },
    {
      header: "Remarks",
      accessorKey: "remarks",
      cell: (info) => info.getValue() || "",
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <TransparentLoader loading={loading} />

      <style>{`
        .tracking-result-area ::selection { background-color: #fecaca !important; color: #000 !important; }
        .tracking-result-area ::-moz-selection { background-color: #fecaca !important; color: #000 !important; }
        .tracking-result-area table tr { user-select: text; -webkit-user-select: text; -moz-user-select: text; -ms-user-select: text; }
        .tracking-result-area .tracking-header { user-select: text; -webkit-user-select: text; -moz-user-select: text; -ms-user-select: text; }
      `}</style>

      <main className="min-h-screen flex items-start justify-center bg-gray-50 pt-6 pb-6 px-4 sm:px-6 tracking-result-area">
        <div className="w-full max-w-6xl bg-white shadow-2xl rounded-2xl p-4 sm:p-6 md:p-10 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <img
              src="http://prod-tqs.s3.ap-south-1.amazonaws.com/vendor_logo/tqs-logo.jpeg"
              alt="Logo"
              className="h-12 sm:h-16 w-auto"
            />
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-gray-600">
              <a
                href="tel:01141518777"
                className="flex items-center gap-2 hover:text-red-600 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2 5.5C2 4.12 3.12 3 4.5 3h1.6c.74 0 1.38.52 1.54 1.24l.64 2.88c.14.62-.18 1.26-.76 1.5l-1.2.5a16.02 16.02 0 007.22 7.22l.5-1.2c.24-.58.88-.9 1.5-.76l2.88.64c.72.16 1.24.8 1.24 1.54v1.6c0 1.38-1.12 2.5-2.5 2.5C8.82 21 2 14.18 2 5.5z" />
                </svg>
                <span>011-41518777</span>
              </a>
              <a href="mailto:info@tqslogistic.com" className="flex items-center gap-2 hover:text-red-600 transition">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M4 6h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z" />
                </svg>
                <span>info@tqslogistic.com</span>
              </a>
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800">
            Track Your Shipment
          </h1>

          <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ values, setFieldValue }) => (
              <Form className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6">

                {/*  Tracking Type Dropdown */}
                <div className="flex flex-col w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tracking Type <span className="text-red-500">*</span>
                  </label>
                  <Field
                    as="select"
                    name="trackingType"
                    className="w-full h-[50px] px-4 border border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-200"
                  >
                    {trackingTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Field>
                </div>

                <div className="flex flex-col w-full md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tracking Number <span className="text-red-500">*</span>
                  </label>
                  <Field
                    type="text"
                    name="trackingNumber"
                    placeholder={`Enter ${trackingTypes.find((t) => t.value === values.trackingType)?.label || "AWB No."}`}
                    className=" w-full h-[50px] px-4  border border-gray-300 rounded-md text-sm text-gray-700  hover:border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-colors duration-200 "
                  />
                </div>

                <div className="flex flex-col justify-end w-full">
                  <button type="submit" className="w-full h-[48px] sm:h-[50px] bg-red-500 hover:bg-red-600 text-white font-semibold rounded-md">
                    TRACK NOW
                  </button>
                </div>
              </Form>
            )}
          </Formik>

          {errorMessage && <div className="text-center text-red-600 font-medium">{errorMessage}</div>}

          {trackingResult && (
            <div className="mt-10 border rounded-xl overflow-hidden shadow">
              <div className="bg-gray-700 text-white p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 tracking-header text-sm">
                <p><strong>Docket No:</strong> {trackingResult.awb || ""}</p>
                <p>
                  <strong>Status:</strong>
                  {trackingResult.current_state && (
                    <span className="bg-green-600 text-white px-2 py-1 rounded text-xs ml-2">
                      {trackingResult.current_state}
                    </span>
                  )}
                </p>
                <p>
                  <strong>Booking Date:</strong>{" "}
                  {trackingResult.booking_date && moment.utc(trackingResult.booking_date).utcOffset("+05:30").format("DD MMM YYYY")}
                </p>
                <p><strong>Origin:</strong> {trackingResult.origin || ""}</p>
                <p><strong>Destination:</strong> {trackingResult.destination || ""}</p>
                <p>
                  <strong>Expected Delivery Date:</strong>{" "}
                  {trackingResult.edd && moment.utc(trackingResult.edd).utcOffset("+05:30").format("DD MMM YYYY")}
                </p>
                <p>
                  <strong>View POD:</strong>{" "}
                  {trackingResult.pod_url && (
                    <a href={trackingResult.pod_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      Click here
                    </a>
                  )}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-[800px] w-full text-sm text-left text-gray-800">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id} className="bg-red-600 text-white text-xs uppercase">
                        {headerGroup.headers.map((header) => (
                          <th key={header.id} className="px-3 sm:px-6 py-2 sm:py-3">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row, idx) => (
                        <tr key={row.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100 transition-colors duration-200`}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="px-3 sm:px-6 py-2 sm:py-3">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={columns.length} className="text-center py-6 text-gray-500">
                          No tracking events found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
